import * as React from 'react';
import {InputRangeProps, InputRangeState} from '../types/components';
import AssetsManager from '../utils/AssetsManager';
import {formatStringToCurrency} from '../../wizard/utils/CurrencyUtils';
import * as ValidateConstant from '../../wizard/constants/ValidateConstant';
import {isValidNumber} from '../utils/ValidationUtils';
import WarningIcon from './WarningIcon';

export default class InputRange extends React.Component<InputRangeProps, InputRangeState> {
    constructor(props) {
        super(props);
        this.state = {
            isFocus: false
        };
    }

    static defaultProps = {
        disabled: false, min: 0, max: 0, id: '',
        colorSelected: '', colorBackGround: '', showValueOnMouseUp: true,
        position: '0', defaultValue: 0, hasError: false, minLength: 0, maxLength: 7,
        labelError: '', required: false, isCurrency: false, currencyFormat: '', countryCurrency: '$', showHelpText: true
    };

    componentDidMount() {
        const {id, max, min, defaultValue} = this.props;
        this.setData(id, max, min, defaultValue);
    }

    componentDidUpdate(prevProps, prevState) {
        const { id, max, min, defaultValue } = this.props;
        const { id: idPrev, max: maxPrev, min: minPrev, defaultValue: prevDefaultValue } = prevProps;
        if (id !== idPrev || max !== maxPrev ||
            min !== minPrev || defaultValue !== prevDefaultValue) {
            this.setData(id, max, min, defaultValue, min);
        }
    }

    setData = (id: string, max: number, min: number, defaultValue: string, defaultMin: number = 0) => {
        const input = document.getElementById(`range_${id}`) as HTMLInputElement;
        input.value = defaultValue.toString() || defaultMin.toString();
        const sendValue: number = (parseInt(input.value || '0', 10) - min) / (max - min);
        this.setGradientStyles(input, sendValue);
    }

    validateEmpty = (value) => {
        return this.props.required && value === '' ? ValidateConstant.ERROR_REQUIRED : null;
    }

    minLenghtValidation = (value) => {
        const minLength = this.props.minLength;
        return minLength && value.toString().length < minLength ? ValidateConstant.ERROR_MIN_LENGTH(minLength) : null;
    }

    validateMinValue = (value: any) => {
        const minValue = this.props.min;
        return minValue != null && parseInt(value, 10) > 0 && parseInt(value, 10) < minValue ?
            ValidateConstant.getMinValueAllowedErrorMessage(minValue) : null;
    }

    validateMaxValue = (value: any) => {
        const maxValue = this.props.max;
        return maxValue != null && parseInt(value, 10) > 0 && parseInt(value, 10) > maxValue ?
            ValidateConstant.getMaxValueAllowedErrorMessage(maxValue) : null;
    }

    focus = (isFocus: boolean = false) => this.setState({...this.state, isFocus});

    onChangeRange = (evt) => {
        this.changeInput(evt);
        this.setState({...this.state, isFocus: false});
    }

    changeInput = (event) => {
        const {id, max, min, onChangeBox, maxLength} = this.props;
        if (!this.state.isFocus) this.setState({...this.state, isFocus: true});
        const input = event.target;
        const value: any = input.value.replace(/\D/g, '').toString().slice(0, maxLength) || '';
        const sendValue = (value - min) / (max - min);
        const inputValue = value;
        const myInput = input.type === 'text' ? document.getElementById(`range_${id}`) as HTMLInputElement : input;
        this.setGradientStyles(myInput, sendValue);

        const error = this.validateEmpty(value) || this.minLenghtValidation(value) || isValidNumber(value) ||
            this.validateMinValue(value) || this.validateMaxValue(value);
        onChangeBox(inputValue, error);
    }

    setGradientStyles = (input: HTMLInputElement, sendValue: number) => {
        const {colorSelected, colorBackGround, disabled} = this.props;
        const color: string = sendValue > 0 && !disabled ? colorSelected : colorBackGround;

        input.style.backgroundImage = `-webkit-gradient(linear, left top, right top, color-stop(${
            sendValue},${colorSelected}), color-stop(${sendValue},${colorBackGround}))`;
        input.style.backgroundImage = this.getGradient('-ms-linear-gradient',
            'left', color, sendValue, colorBackGround);
        input.style.backgroundImage = this.getGradient('-moz-linear-gradient',
            'left', color, sendValue, colorBackGround);
        input.style.backgroundImage = this.getGradient('-o-linear-gradient',
            'left', color, sendValue, colorBackGround);
        input.style.backgroundImage = this.getGradient('-webkit-linear-gradient',
            'left', color, sendValue, colorBackGround);
        input.style.backgroundImage = this.getGradient('linear-gradient',
            'to right', color, sendValue, colorBackGround);
    }

    getGradient = (gradient: string, direction: string, color: string, sendValue: number, backColor: string) => (
        `${gradient}(${direction}, ${color} ${sendValue * 100}%, ${backColor} 0%)`
    )

    renderHelpOrErrorText = (hasError: boolean, labelError: string, invalidClass: string, helpText: string) => {
        const id: string = this.props.id;
        if (hasError && labelError) {
            return <strong id={`${id}Help`} className={`form-text ${invalidClass}`}>
                {labelError}
            </strong>;
        }
        if (helpText) {
            return helpText && (<strong id={`${id}Help`} className={'form-text'}>{helpText}</strong>);
        }
    }

    render() {
        const {
            id, max, min, defaultValue, position, disabled, helpText,
            labelError, label, hasError, required, inputCustomClass, maxLength,
            isCurrency, countryCurrency, step, minLength, showValueOnMouseUp, showHelpText
        } = this.props;

        const isFocus: boolean = this.state.isFocus;

        const invalidClass = hasError && labelError ? 'is-invalid' : '';
        const disabledClass = disabled ? 'text-secondary-lighten-2' : '';

        const inputClasses = [
            'w-100',
            invalidClass,
            disabledClass,
            inputCustomClass
        ].join(' ');
        const helpOrErrorText = this.renderHelpOrErrorText(hasError, labelError, invalidClass, helpText);
        const formatCurrency = formatStringToCurrency(defaultValue, countryCurrency);
        return <div className="form-group custom-input input-label">
            <label htmlFor={`range_${id}`}>{label}{required && <span className="asterisk">*</span>}
                {hasError && labelError && <span className="ic"><WarningIcon/></span>}
            </label>
            <input type="text"
                   className={`range-label text-right form-control range-input ${
                       disabled ? 'disable-range-input' : ''}`}
                   id={`lbl_${id}`}
                   minLength={minLength}
                   maxLength={maxLength}
                   required={required}
                   disabled={disabled}
                   value={!isFocus && isCurrency ? formatCurrency : defaultValue}
                   onChange={this.changeInput}
                   onFocus={() => this.focus(true)}
                   onBlur={() => this.focus(false)}
            />
            <input className={inputClasses}
                   data-position={position} data-delay="50" type="range"
                   onChange={showValueOnMouseUp ? () => {
                   } : this.onChangeRange}
                   onMouseUp={showValueOnMouseUp ? this.onChangeRange : () => {
                   }}
                   onTouchEnd={showValueOnMouseUp ? this.onChangeRange : () => {
                   }}
                   id={`range_${id}`} min={min} max={max}
                   step={step}
                   defaultValue={defaultValue.toString()}
                   disabled={disabled}/>
            {!disabled && showHelpText && helpOrErrorText}
        </div>;
    }
}
